/** TravelTrucks OS · Desk Layout PROFILES
 *  ─────────────────────────────────────────────────────────────────────
 *  MJ uses the Desk on multiple screens — office ultrawide (3840×1080),
 *  laptop, and phone. One saved layout can't serve all three. Profiles
 *  let a user save a different tile ARRANGEMENT per screen while
 *  reading the SAME underlying data everywhere.
 *
 *  This module owns:
 *    - viewport → breakpoint detection ("ultrawide" | "desktop" | "phone")
 *    - useActiveProfile() hook — chooses the right profile ID for the
 *      current device with a per-device manual-override remembered in
 *      localStorage, so MJ's explicit choice sticks and doesn't get
 *      re-guessed after every reload.
 *    - <ProfileSwitcher> — the segmented top-bar control (create,
 *      switch, duplicate, rename, delete).
 *
 *  Backend contract (see desk_os.py):
 *    GET  /api/admin/desk/profiles              → { profiles: [...] }
 *    POST /api/admin/desk/profiles              → { profile_id, name }
 *    PATCH /api/admin/desk/profiles/{pid}       → rename / auto_kind
 *    DELETE /api/admin/desk/profiles/{pid}      → remove (never "default")
 *    GET  /api/admin/desk/layout?profile=<pid>  → layout for that profile
 *    PUT  /api/admin/desk/layout?profile=<pid>  → save layout for that profile
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const LS_DEVICE_CHOICE = 'kindra.desk.profile.byDevice';   // { [breakpoint]: profile_id }

// ── Viewport breakpoint detection ─────────────────────────────────────
// Ultrawide = wide + short-ish. 3840×1080 is aspect 3.55 : 1, a laptop
// 1440×900 is 1.6 : 1, a phone in portrait ≈ 0.46 : 1. We treat anything
// >= 2400px OR aspect >= 2.4 as ultrawide, < 768 as phone, else desktop.
export const detectBreakpoint = () => {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth || document.documentElement.clientWidth || 0;
  const h = window.innerHeight || document.documentElement.clientHeight || 1;
  const aspect = w / h;
  if (w >= 2400 || aspect >= 2.4) return 'ultrawide';
  if (w < 768) return 'phone';
  return 'desktop';
};

// ── Small localStorage helper — never throws ──────────────────────────
const _readDeviceChoices = () => {
  try { return JSON.parse(localStorage.getItem(LS_DEVICE_CHOICE) || '{}') || {}; }
  catch { return {}; }
};
const _writeDeviceChoice = (breakpoint, profileId) => {
  try {
    const cur = _readDeviceChoices();
    cur[breakpoint] = profileId;
    localStorage.setItem(LS_DEVICE_CHOICE, JSON.stringify(cur));
  } catch (_e) { /* noop */ }
};

// ── Hook: manages the active profile + auto-pick rules ────────────────
export const useActiveProfile = (req) => {
  const [profiles, setProfiles] = useState([]);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [breakpoint, setBreakpoint] = useState(() => detectBreakpoint());
  const [activeId, setActiveId] = useState(null);   // null = still resolving
  const [manuallyOverridden, setManuallyOverridden] = useState(false);

  // Track viewport class — flip only when the CATEGORY changes so we
  // don't thrash on every resize pixel.
  useEffect(() => {
    const onResize = () => {
      const next = detectBreakpoint();
      if (next !== breakpoint) setBreakpoint(next);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);

  // Load the profile registry once (and refresh explicitly after mutations).
  const refresh = useCallback(async () => {
    try {
      const d = await req('GET', '/api/admin/desk/profiles');
      setProfiles(d.profiles || []);
      setProfilesLoaded(true);
      return d.profiles || [];
    } catch (_e) {
      setProfilesLoaded(true);   // fail-open so UI doesn't hang
      return [];
    }
  }, [req]);
  useEffect(() => { refresh(); }, [refresh]);

  // Auto-pick the best profile for this breakpoint once profiles are known.
  // Preference order:
  //   1. Per-device manual override (localStorage).
  //   2. A profile whose `auto_kind` matches the breakpoint.
  //   3. The default profile.
  useEffect(() => {
    if (!profilesLoaded || !profiles.length) return;
    const deviceChoices = _readDeviceChoices();
    const stored = deviceChoices[breakpoint];
    if (stored && profiles.some((p) => p.profile_id === stored)) {
      setActiveId(stored);
      setManuallyOverridden(true);
      return;
    }
    const kindMatch = profiles.find((p) => p.auto_kind === breakpoint);
    setActiveId(kindMatch?.profile_id || 'default');
    setManuallyOverridden(false);
  }, [profilesLoaded, profiles, breakpoint]);

  const switchTo = useCallback((profileId) => {
    setActiveId(profileId);
    setManuallyOverridden(true);
    _writeDeviceChoice(breakpoint, profileId);
  }, [breakpoint]);

  const createProfile = useCallback(async ({ name, autoKind, cloneFrom }) => {
    const d = await req('POST', '/api/admin/desk/profiles', {
      name, auto_kind: autoKind || 'custom', clone_from: cloneFrom || activeId,
    });
    await refresh();
    if (d.profile_id) switchTo(d.profile_id);
    return d;
  }, [req, refresh, activeId, switchTo]);

  const renameProfile = useCallback(async (pid, name) => {
    await req('PATCH', `/api/admin/desk/profiles/${pid}`, { name });
    await refresh();
  }, [req, refresh]);

  const deleteProfile = useCallback(async (pid) => {
    if (pid === 'default') return { ok: false, reason: 'Cannot delete the default profile' };
    await req('DELETE', `/api/admin/desk/profiles/${pid}`);
    if (activeId === pid) {
      // fall back to auto-pick for this breakpoint
      const deviceChoices = _readDeviceChoices();
      delete deviceChoices[breakpoint];
      try { localStorage.setItem(LS_DEVICE_CHOICE, JSON.stringify(deviceChoices)); } catch (_e) { /* noop */ }
      setManuallyOverridden(false);
      setActiveId(null);   // triggers re-pick after refresh
    }
    await refresh();
    return { ok: true };
  }, [req, refresh, activeId, breakpoint]);

  return {
    profiles, activeId, breakpoint, profilesLoaded, manuallyOverridden,
    switchTo, createProfile, renameProfile, deleteProfile, refresh,
  };
};

// ── The visible switcher (top-bar control) ────────────────────────────
const PILL_BTN = {
  background: 'transparent',
  border: '1px solid rgba(241,242,243,0.14)',
  color: '#F1F2F3', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12,
  padding: '5px 10px', borderRadius: 999, cursor: 'pointer',
};

export const ProfileSwitcher = ({ profile }) => {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);   // {pid,name}
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const active = useMemo(() =>
    profile.profiles.find((p) => p.profile_id === profile.activeId) || null,
    [profile.profiles, profile.activeId]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!e.target.closest('[data-testid="profile-switcher"]')) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!profile.profilesLoaded) return null;

  const commitRename = async () => {
    if (renaming && renaming.name.trim()) {
      await profile.renameProfile(renaming.pid, renaming.name.trim());
    }
    setRenaming(null);
  };

  return (
    <div data-testid="profile-switcher" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button data-testid="profile-switcher-open"
        onClick={() => setOpen((v) => !v)}
        title={`Layout profile — ${profile.breakpoint}${profile.manuallyOverridden ? ' · manual' : ' · auto'}`}
        style={{ ...PILL_BTN, display: 'inline-flex', alignItems: 'center', gap: 6,
                 background: 'rgba(236,124,36,0.14)', borderColor: 'rgba(236,124,36,0.42)' }}>
        <span style={{ color: '#FFB469', fontWeight: 700 }}>⧉</span>
        <span>{active?.name || 'Layout'}</span>
        <span style={{ color: '#A7ABB2', fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div data-testid="profile-switcher-menu"
          style={{ position: 'absolute', top: '110%', right: 0, minWidth: 240, zIndex: 200,
                   background: 'rgba(14,15,17,0.96)', backdropFilter: 'blur(12px)',
                   border: '1px solid rgba(241,242,243,0.14)', borderRadius: 12,
                   boxShadow: '0 24px 60px rgba(0,0,0,0.5)', padding: 6 }}>
          <div style={{ padding: '6px 10px 4px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 9.5,
                        letterSpacing: '0.14em', textTransform: 'uppercase', color: '#A7ABB2' }}>
            Layout profiles · viewport {profile.breakpoint}
          </div>
          {profile.profiles.map((p) => (
            <div key={p.profile_id} style={{ display: 'flex', alignItems: 'center', gap: 4,
                                              padding: '4px 4px', borderRadius: 8,
                                              background: p.profile_id === profile.activeId ? 'rgba(236,124,36,0.14)' : 'transparent' }}>
              {renaming?.pid === p.profile_id ? (
                <input autoFocus value={renaming.name}
                  data-testid={`profile-rename-input-${p.profile_id}`}
                  onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                  onBlur={commitRename}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(null); }}
                  style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(241,242,243,0.24)',
                           borderRadius: 6, padding: '4px 8px', color: '#F1F2F3', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12 }} />
              ) : (
                <button data-testid={`profile-select-${p.profile_id}`}
                  onClick={() => { profile.switchTo(p.profile_id); setOpen(false); }}
                  style={{ flex: 1, textAlign: 'left', padding: '6px 10px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12,
                           color: p.profile_id === profile.activeId ? '#F1F2F3' : '#A7ABB2',
                           background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 6 }}>
                  {p.name}
                  <span style={{ marginLeft: 6, fontSize: 9.5, color: '#5F636B' }}>
                    · {p.auto_kind}{p.profile_id === profile.activeId ? ' · active' : ''}
                  </span>
                </button>
              )}
              {!renaming && p.profile_id !== 'default' && (
                <>
                  <button data-testid={`profile-rename-${p.profile_id}`}
                    title="Rename"
                    onClick={() => setRenaming({ pid: p.profile_id, name: p.name })}
                    style={{ background: 'transparent', border: 'none', color: '#A7ABB2', cursor: 'pointer', padding: '2px 6px', fontSize: 12 }}>✎</button>
                  <button data-testid={`profile-delete-${p.profile_id}`}
                    title="Delete profile · data untouched, only removes this arrangement"
                    onClick={async () => {
                      if (!window.confirm(`Delete layout "${p.name}"? Your Desk data is untouched — only this arrangement is removed.`)) return;
                      await profile.deleteProfile(p.profile_id);
                    }}
                    style={{ background: 'transparent', border: 'none', color: '#EC7C24', cursor: 'pointer', padding: '2px 6px', fontSize: 12 }}>✕</button>
                </>
              )}
            </div>
          ))}
          <div style={{ borderTop: '1px solid rgba(241,242,243,0.08)', marginTop: 6, paddingTop: 6 }}>
            {creating ? (
              <div style={{ display: 'flex', gap: 4, padding: '2px 6px' }}>
                <input autoFocus value={newName}
                  data-testid="profile-create-input"
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={`Duplicate "${active?.name || 'active'}" as…`}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && newName.trim()) {
                      await profile.createProfile({ name: newName.trim(), autoKind: profile.breakpoint, cloneFrom: profile.activeId });
                      setNewName(''); setCreating(false); setOpen(false);
                    }
                    if (e.key === 'Escape') { setCreating(false); setNewName(''); }
                  }}
                  onBlur={() => { setCreating(false); setNewName(''); }}
                  style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(241,242,243,0.24)',
                           borderRadius: 6, padding: '4px 8px', color: '#F1F2F3', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12 }} />
              </div>
            ) : (
              <button data-testid="profile-duplicate"
                onClick={() => { setCreating(true); setNewName(''); }}
                style={{ width: '100%', textAlign: 'left', padding: '6px 10px', fontFamily: 'Source Sans 3, sans-serif', fontSize: 12,
                         color: '#54D197', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                ＋ Duplicate active → new profile
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
